import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect } from 'react';
import panzoom from 'panzoom'
import Caixa from './Caixa'
import Text from './Text';
import SelectableCaixa from './selectableCaixa';
import RotationHandle from './RotationHandle';
import './Pissarra.css'

import logo from './data/logo.png';
import { isDesktop, isMobile } from 'react-device-detect';
import { getSubdomain } from '../../utils/utils';
import SETTINGS from '../../SETTINGS';
import TroncCalculations from './TroncCalculations';
import { getBoxCenter, calculateSnap } from './snapUtils';
import useSelectionRotation from './useSelectionRotation';

const COLLA = getSubdomain()

const settings_nodoubletap = {
    maxZoom: 4,
    minZoom: 0.25,
    initialZoom: 1,
    zoomDoubleClickSpeed: 1,
    initialX: -100,
    initialY: 600
};

function Logo({ opacity, socket, castell, minX, minY, center_pinya }) {
    useSocketReconnect(socket, () => {
        if (castell && !castellName) socket.emit('.request_bundle', castell);
    });

    const [castellName, setCastellName] = useState('');

	const styles = {
		'opacity': opacity*0.2,
        'transform': `translate(${minX - 300}px, ${minY - 300}px)`,
	};

    useEffect(() => {
        setCastellName('');
        const onBundle = ({ id, bundle }) => id === castell && setCastellName(bundle.nom);
        socket.on('.bundle', onBundle)
        socket.emit('.request_bundle', castell)

        return () => socket.off('.bundle', onBundle)
    }, [castell, socket])

    useEffect(() => {
        document.title = `${castellName} - Aleta`
    }, [castellName])

	return (
		<div style={{
            ...styles,
            display: 'flex',
            position: 'absolute',
        }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
			    <img src={`/logos/${COLLA}.png`} width='250px' />
            </div>

            <div
                style={{
                    maxWidth: 300,
                    marginLeft: 30,
                }}
            >
                <h1>{castellName}</h1>
                <h2>{SETTINGS(COLLA).fullname}</h2>
            </div>
		</div>
	);
}

function PissarraContainer({ children, setCaixaSelected, readonly, panzoom, followPosition, setFollowPosition, json, setJsonOutput, setPosition, setSvg, onPointerDown, onPointerMove, onPointerUp, selectionBox, mode, caixesCount, ...props }) {
    const [pointerPos, setPointerPos] = useState([0,0]);

    const transform = panzoom ? panzoom.getTransform() : null;
    
    const floating_go_back_button_style = {
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 100,
        padding: '10px',
        backgroundColor: 'white',
        borderRadius: '5px',
        cursor: 'pointer',
        border: '1px solid black',
        textDecoration: 'none',
        color: 'black',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    };

    const handleGoBack = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = '/editor';
        }
    };

    // Success notification
    const floating_notification_style = {
        position: 'absolute',
        top: '10px',
        right: '12%',
        zIndex: 100,
        borderRadius: '5px',
        padding: '10px 20px',
        display: 'none',
        backgroundColor: 'rgba(0, 255, 0, 0.25)',
        color: '#777',
    };

    // Box counter style
    const floating_box_count_style = {
        position: 'absolute',
        top: '10px',
        left: '130px',
        zIndex: 100,
        borderRadius: '5px',
        padding: '10px 15px',
        backgroundColor: '#eee',
        color: '#333',
        fontSize: '14px',
        fontWeight: 'bold',
    };

    useEffect(() => {
        if (props.notification) {
            setTimeout(() => props.setNotification(null), 3000);
        }
    }, [props.notification])

    return (
        <div 
            className={`pissarra-container ${mode === 'transform' ? 'transform-mode' : ''}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
        >
            <a 
                href="/editor" 
                style={floating_go_back_button_style}
                onClick={handleGoBack}
                onTouchEnd={handleGoBack}
            >
                ⬅️ Tornar
            </a>

            <div style={floating_box_count_style}>
                👤 {caixesCount}
            </div>

            <div style={{...floating_notification_style, ['display']: props.notification ? 'block' : 'none'}}>
                {props.notification}
            </div>

            { selectionBox && (
                <div style={{
                    position: 'fixed',
                    left: Math.min(selectionBox.startX, selectionBox.endX),
                    top: Math.min(selectionBox.startY, selectionBox.endY),
                    width: Math.abs(selectionBox.endX - selectionBox.startX),
                    height: Math.abs(selectionBox.endY - selectionBox.startY),
                    border: '1px dashed #007bff',
                    backgroundColor: 'rgba(0, 123, 255, 0.1)',
                    pointerEvents: 'none',
                    zIndex: 9999
                }} />
            )}

            { children }
        </div>
    );
}

function PissarraEditor(props) {
    const { selectedBundle, pestanya, socket, json, selectedCastellPart, setPanzoom, readonly, snapEnabled } = props;
    const [followPosition, setFollowPosition] = useState(false);
    const [position, setPosition] = useState(null);
    const [transformOrigin, setTransformOrigin] = useState([0,0]);
    const [initializationOpacity, setInitializationOpacity] = useState(0);

    // Selection & Dragging State
    const [selectionBox, setSelectionBox] = useState(null);
    const [dragState, setDragState] = useState(null);
    const [snapLines, setSnapLines] = useState({ x: null, y: null });
    const {
        isRotating,
        isRotationInProgress,
        handleRotationStart
    } = useSelectionRotation({
        json,
        mode: props.mode,
        pz: props.pz,
        readonly,
        selectedIds: props.selectedIds,
        setJsonOutput: props.setJsonOutput
    });

    useEffect(() => {
        if (!snapEnabled) {
            setSnapLines({ x: null, y: null });
        }
    }, [snapEnabled]);

    // Panzoom
    useEffect(() => {
        const movable = document.querySelector('#movable-content');
        setPanzoom(panzoom(movable, settings_nodoubletap));
        return () => props.pz ? props.pz.dispose() : null;
    }, []);

    const handlePointerDown = (ev) => {
        const { clientX, clientY } = ev;
        // Check if target is background or pissarra-container
        // Only allow selection box when submode is 'select'
        if (props.mode === 'transform' && props.submode === 'select' && !ev.shiftKey && (ev.target.classList?.contains('pissarra-container') || ev.target.id === 'movable-content' || ev.target.id === 'pissarra-content')) {
            // Start selection box
            if (props.pz) props.pz.pause();
            setSelectionBox({
                startX: clientX,
                startY: clientY,
                endX: clientX,
                endY: clientY
            });
            // Clear selection if not shifting
            if (!ev.shiftKey) {
                props.setCaixaSelected(-1);
                props.setSelectedIds([]);
            }
        }
    };

    const handlePointerMove = (ev) => {
        const { clientX, clientY } = ev;
        const transform = props.pz ? props.pz.getTransform() : { x: 0, y: 0, scale: 1 };

        if (selectionBox) {
            setSelectionBox(prev => ({
                ...prev,
                endX: clientX,
                endY: clientY
            }));
        } else if (dragState) {
            let dx = (clientX - dragState.startX) / transform.scale;
            let dy = (clientY - dragState.startY) / transform.scale;

            const draggedCenters = (dragState.draggedCenters || [])
                .map(center => ({
                    x: center.x + dx,
                    y: center.y + dy
                }));

            let snapLineState = { x: null, y: null };

            // Calculate snap adjustments
            if (snapEnabled) {
                const snap = calculateSnap(draggedCenters, dragState.otherCenters || [], dx, dy);
                dx = snap.dx;
                dy = snap.dy;
                snapLineState = { x: snap.snapLineX, y: snap.snapLineY };
            }

            // Update snap lines for visualization
            setSnapLines(snapLineState);

            const newJson = { ...json };
            
            // Apply delta to all selected boxes
            Object.keys(dragState.initialBoxes).forEach(id => {
                const initialBox = dragState.initialBoxes[id];
                if (initialBox) {
                    const [a, b, c, d, e, f] = initialBox.transform;
                     if (newJson[id]) {
                        newJson[id] = {
                            ...newJson[id],
                            box: {
                                ...newJson[id].box,
                                transform: [a, b, c, d, e + dx, f + dy]
                            }
                        };
                     }
                }
            });
            props.setJsonOutput(newJson, false); 
        }
    };

    const handlePointerUp = (ev) => {
        if (isRotationInProgress()) return;

        if (selectionBox) {
            // Finalize selection
            if (props.pz) props.pz.resume();

            const rect = {
                left: Math.min(selectionBox.startX, selectionBox.endX),
                top: Math.min(selectionBox.startY, selectionBox.endY),
                right: Math.max(selectionBox.startX, selectionBox.endX),
                bottom: Math.max(selectionBox.startY, selectionBox.endY)
            };

            // Find boxes inside rect
            const selected = [];
            const caixes_keys = Object.keys(json).filter(key => json[key].type === 'caixa');
            const text_keys = Object.keys(json).filter(key => json[key].type === 'text');
            
            caixes_keys.forEach(key => {
                const caixaElement = document.getElementById(`caixa-${key}`);
                if (caixaElement) {
                    const boxRect = caixaElement.getBoundingClientRect();
                    const centerX = boxRect.left + boxRect.width / 2;
                    const centerY = boxRect.top + boxRect.height / 2;

                    if (centerX >= rect.left && centerX <= rect.right &&
                        centerY >= rect.top && centerY <= rect.bottom) {
                        selected.push(key);
                    }
                }
            });

            // Include text elements inside rect
            text_keys.forEach(key => {
                const textElement = document.getElementById(`text-${key}`);
                if (textElement) {
                    const boxRect = textElement.getBoundingClientRect();
                    const centerX = boxRect.left + boxRect.width / 2;
                    const centerY = boxRect.top + boxRect.height / 2;

                    if (centerX >= rect.left && centerX <= rect.right &&
                        centerY >= rect.top && centerY <= rect.bottom) {
                        selected.push(key);
                    }
                }
            });

            if (selected.length > 0) {
                props.setSelectedIds(prev => [...new Set([...prev, ...selected])]);
                if (selected.length === 1 && json[selected[0]]?.type === 'caixa') props.setCaixaSelected(selected[0]);
            }

            setSelectionBox(null);
            // Turn off selection mode after completing a selection
            props.setSubmode(null);
        } else if (dragState) {
            // Finalize drag
            if (props.pz) props.pz.resume();
            setDragState(null);
            setSnapLines({ x: null, y: null });
            // Save final state to history
            props.setJsonOutput(prev => prev, true);
        } else {
             // Normal click on background - deselect all
             if (ev.target.classList?.contains('pissarra-container') || ev.target.id === 'movable-content' || ev.target.id === 'pissarra-content') {
                props.setCaixaSelected(-1);
                props.setSelectedIds([]);
             }
        }
    };

    const handleDragStart = (ev, id) => {
        if (readonly) return;
        
        let newSelectedIds = props.selectedIds || [];
        
        if (!newSelectedIds.includes(id)) {
            if (!ev.shiftKey) {
                newSelectedIds = [id];
                if (json[id]?.type === 'caixa') {
                    props.setCaixaSelected(id);
                } else {
                    props.setCaixaSelected(-1);
                }
                props.setSelectedIds([id]);
            } else {
                newSelectedIds = [...newSelectedIds, id];
                props.setSelectedIds(prev => [...prev, id]);
            }
        }

        const initialBoxes = {};
        newSelectedIds.forEach(selectedId => {
            if (json[selectedId]) {
                initialBoxes[selectedId] = json[selectedId].box;
            }
        });

        const draggedIds = Object.keys(initialBoxes);

        // Pre-compute centers to avoid recalculating them on every pointer move
        const draggedCenters = draggedIds
            .map(id => initialBoxes[id])
            .filter(Boolean)
            .map(box => getBoxCenter(box));

        const otherCenters = Object.entries(json)
            .filter(([otherId, val]) => val?.box && !draggedIds.includes(otherId))
            .map(([, val]) => getBoxCenter(val.box));

        if (props.pz) props.pz.pause();
        setDragState({
            startX: ev.clientX,
            startY: ev.clientY,
            initialBoxes,
            draggedCenters,
            otherCenters
        });
    };

    const exports = {
        'caixaSelected': props.caixaSelected,
        'setCaixaSelected': props.setCaixaSelected,
        'castellersInfo': props.castellersInfo,
        'readonly': props.readonly,
        'followPosition': followPosition,
        'setFollowPosition': setFollowPosition,
        'position': position,
        'setPosition': setPosition,
        'handleDragStart': handleDragStart,
        ...props
    };

    const plantilla_settings = json['settings'] || {};
    const caixes_json = [...Object.entries(json)].filter(el => el[1] && el[1].type === 'caixa');
    const perfiled_caixes = caixes_json.filter(([key, val]) => !pestanya ? true : val?.pestanya.toLowerCase() === pestanya.toLowerCase());
    const caixes_keys = perfiled_caixes.map(el => el[0]);

    const escaletes = [...Object.entries(json)]
        .filter(el => el[1] && el[1].type === 'caixa')
        .filter(([id, attrs]) => attrs.escala_a && Array.isArray(attrs.escala_a) && attrs.escala_a.length > 0)
        .map(([id, attrs]) => attrs.escala_a.map(escalat => ({
            escalador: id,
            escalat: escalat
        })))
        .flat()

    const trepitjacions = [...Object.entries(json)]
        .filter(el => el[1] && el[1].type === 'caixa')
        .filter(([id, attrs]) => attrs.trepitja_a && Array.isArray(attrs.trepitja_a) && attrs.trepitja_a.length > 0)
        .map(([id, attrs]) => attrs.trepitja_a.map(trepitjat => ({
            trepitjador: id,
            trepitjat: trepitjat
        })))
        .flat()

    const caixes = caixes_keys.map(caixa_id => {
        const trepitjadors = trepitjacions
            .filter(trepitjacio => trepitjacio.trepitjat === caixa_id)
            .map(trepitjacio => trepitjacio.trepitjador)

        const isTrepitjador = json[caixa_id]?.trepitja_a?.length > 0

        const escaladors = escaletes
            .filter(escaleta => escaleta.escalat === caixa_id)
            .map(escaleta => escaleta.escalador)

        const isEscaleta = json[caixa_id]?.escala_a?.length > 0

        return <Caixa
            {...json[caixa_id]}
            {...exports}
            key={caixa_id}
            id={caixa_id}
            boxProps={json[caixa_id]["box"]}    
            textProps={json[caixa_id]["text"]}  
            className="noselectable"
            fill={json[caixa_id].fill || 'white'}
            disabled={'clone' in json[caixa_id]}
            fontSize={plantilla_settings.fontSize || 14}
            opacity={initializationOpacity}
            escaladors={escaladors}
            trepitjadors={trepitjadors}
            isEscaleta={isEscaleta}
            isTrepitjador={isTrepitjador}
        />;
    })

    const textos_json = [...Object.entries(json)].filter(el => el[1] && el[1].type === 'text');
    const textos_keys = textos_json.map(el => el[0]);

    const handleTextDoubleClick = (text_id) => {
        if (readonly) return;
        const currentValue = json[text_id]?.value || '';
        const newValue = window.prompt('Edita el text:', currentValue);
        if (newValue !== null && newValue !== currentValue) {
            props.setJsonOutput({
                ...json,
                [text_id]: {
                    ...json[text_id],
                    value: newValue
                }
            });
        }
    };

    const textos = textos_keys.map(text_id => {
        return (
            <SelectableCaixa
                key={text_id}
                id={text_id}
                mode={props.mode}
                submode={props.submode}
                setSelectedIds={props.setSelectedIds}
                selectedIds={props.selectedIds}
                setCaixaSelected={props.setCaixaSelected}
                handleDragStart={handleDragStart}
                shouldSetCaixaSelected={false}
                onDoubleClick={() => handleTextDoubleClick(text_id)}
            >
                <Text
                    id={text_id}
                    textValue={json[text_id].value}
                    boxProps={json[text_id].box}
                    textProps={json[text_id].text}
                />
            </SelectableCaixa>
        );
    });

    useEffect(() => {
        const baixos = caixes_json
            .filter(([key, val]) => val?.perfil?.toLowerCase() === 'baix')

        const centre_baixos = baixos.length > 0 ?
            baixos
                .filter(([key, val]) => val?.box?.transform)
                .map(([key, val]) => val.box.transform)
                .reduce((vsum, t) =>
                    [vsum[0] + t[4], vsum[1] + t[5]]
                , [0, 0])
                .map(el => el / baixos.length)
                .map(el => el + baixos[0][1].box.width / 2)
        :
            caixes_json
                .filter(([key, val]) => val?.box?.transform)
                .map(([key, val]) => val.box)
                .map(({ transform, width, height }) => {
                    const [a, b, c, d, e, f] = transform;
                    const theta = Math.atan2(b, a); // rotation angle
            
                    // rotate the width and height vectors by theta
                    const centerOffsetX = (width / 2) * Math.cos(theta) - (height / 2) * Math.sin(theta);
                    const centerOffsetY = (width / 2) * Math.sin(theta) + (height / 2) * Math.cos(theta);

                    // apply the affine transformation to find the center point in the global coordinates
                    const centerX = a * centerOffsetX + c * centerOffsetY + e;
                    const centerY = b * centerOffsetX + d * centerOffsetY + f;
            
                    return [centerX, centerY];
                })
                .reduce((vsum, t) => [vsum[0] + t[0], vsum[1] + t[1]], [0, 0])
                .map(el => caixes_json.length === 0 ? 0 : el / caixes_json.length)

        setTransformOrigin(centre_baixos)
    }, [caixes_json.length])

    const center_screen = [window.innerWidth / 2, window.innerHeight / 2]
    const center_pinya = [
        center_screen[0] - transformOrigin[0],
        center_screen[1] - transformOrigin[1],
    ]

    const caixesPositions = caixes_json
        .filter(([key, val]) => val?.box?.transform)
        .map(([key, val]) => val.box.transform)
        .map(([a, b, c, d, e, f]) => [e, f])

    const {minX, maxX, minY, maxY} = caixesPositions.reduce((acc, [x, y]) => ({
        minX: Math.min(acc.minX, x),
        maxX: Math.max(acc.maxX, x),
        minY: Math.min(acc.minY, y),
        maxY: Math.max(acc.maxY, y)
    }), {minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity});

    useEffect(() => {
        const longestDistBetweenCaixes = { 
            horizontal: maxX - minX + 200, 
            vertical: maxY - minY + 200
        };

        const scale = Math.min(
            window.innerWidth / longestDistBetweenCaixes.horizontal,
            window.innerHeight / longestDistBetweenCaixes.vertical
        ) * 0.9

        if (props.pz && caixes_json.length > 0) {
            // Smoothly zoom to center of screen
            props.pz.smoothZoom(
                center_screen[0],
                center_screen[1],
                isMobile ? 0.9 : scale
            )

            // Get opacity to 1
            const interval = setInterval(() => {
                setInitializationOpacity(prev => Math.min(prev + 0.05, 1))
            }, 100)

            setTimeout(() => {
                clearInterval(interval)
                setInitializationOpacity(1)
            }, 2000)
        }
    }, [
        JSON.stringify(transformOrigin) === JSON.stringify([0,0]),
        caixes_json.length === 0,
        props.pz
    ])

    if (json === 'NOT_EXISTS') {
        return (
            <div>
                <p>ERROR: la plantilla del castell ({selectedCastellPart}) no existeix. Crea-la amb el creador de pinyes.</p>
            </div>
        );
    } else {
        return (
            <PissarraContainer 
                setCaixaSelected={props.setCaixaSelected} 
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                selectionBox={selectionBox}
                caixesCount={caixes_keys.length}
                {...exports}
            >
                <div id="movable-content">
                    <div id="pissarra-content">
                        <div
                            className='pissarra'
                            style={{
                                transformOrigin: `${transformOrigin[0]}px ${transformOrigin[1]}px`,
                                transform: `translate(${isDesktop && !readonly ? center_pinya[0] - 50 : center_pinya[0]}px, ${isMobile && !readonly ? center_pinya[1] - 50 : center_pinya[1]}px) rotate(${0}deg)`
                            }}
                        >
                            {
                                selectedBundle && (
                                    <Logo
                                        castell={selectedBundle}
                                        loaded={Object.keys(json).length > 0}
                                        socket={socket}
                                        minX={minX}
                                        minY={minY}
                                        center_pinya={center_pinya}
                                        opacity={initializationOpacity}
                                    />
                                )
                            }
                            {caixes}
                            {textos}

                            {/* Snap guide lines */}
                            {snapLines.x !== null ? (
                                <div style={{
                                    position: 'absolute',
                                    left: snapLines.x,
                                    top: minY - 500,
                                    width: 1,
                                    height: (maxY - minY) + 1000,
                                    backgroundColor: '#007bff',
                                    pointerEvents: 'none',
                                    zIndex: 9998
                                }} />
                            ) : null}
                            {snapLines.y !== null ? (
                                <div style={{
                                    position: 'absolute',
                                    left: minX - 500,
                                    top: snapLines.y,
                                    width: (maxX - minX) + 1000,
                                    height: 1,
                                    backgroundColor: '#007bff',
                                    pointerEvents: 'none',
                                    zIndex: 9998
                                }} />
                            ) : null}

                            {
                                selectedBundle && <TroncCalculations
                                    json={json}
                                    opacity={initializationOpacity}
                                    {...props}
                                />
                            }
                        </div>
                    </div>
                </div>
                <RotationHandle
                    json={json}
                    selectedIds={props.selectedIds}
                    mode={props.mode}
                    readonly={readonly}
                    isRotating={isRotating}
                    onPointerDown={handleRotationStart}
                />
            </PissarraContainer>
        );
    }
}

export default PissarraEditor;
